import React, { useRef } from 'react';
import { Box, Chip, Typography, Badge, useTheme, Tooltip } from '@mui/material';
import moment from 'moment';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';

export interface DemandItem {
  id: string;
  dateStr: string; // 'YYYY-MM-DD'
  cargoId: string;
  cargoName: string;
  turnoId: string;
  turnoName: string;
  cantidad: number;
  color?: string;
}

interface Props {
  demands: DemandItem[];
  onDateSelect: (start: Date, end: Date) => void;
  onDateClick: (date: Date) => void;
  onEventClick: (demand: DemandItem) => void;
}

const EVENT_LIMIT = 3;

export const CalendarPlanner = ({ demands, onDateSelect, onDateClick, onEventClick }: Props) => {
  const theme = useTheme();
  const calendarRef = useRef<any>(null);

  // Group demands by date and turnos to display efficiently
  const events = demands.map((d) => ({
    id: d.id,
    title: `${d.cargoName} · ${d.turnoName} · ${d.cantidad}`,
    date: d.dateStr,
    extendedProps: { ...d },
  }));

  const renderEventContent = (eventInfo: any) => {
    const d = eventInfo.event.extendedProps as DemandItem;
    return (
      <Tooltip title={eventInfo.event.title} arrow placement="top">
        <Box sx={{ p: '2px 4px', width: '100%', overflow: 'hidden' }}>
          <Chip
            label={
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                {eventInfo.event.title}
              </Typography>
            }
            size="small"
            sx={{
              height: 20,
              width: '100%',
              justifyContent: 'flex-start',
              bgcolor: d.color || theme.palette.primary.light,
              color: theme.palette.primary.contrastText,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        </Box>
      </Tooltip>
    );
  };

  const renderDayCellContent = (arg: any) => {
    const dateStr = moment(arg.date).format('YYYY-MM-DD');
    const dayDemands = demands.filter((d) => d.dateStr === dateStr);
    const totalPersonas = dayDemands.reduce((acc, curr) => acc + curr.cantidad, 0);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        <Typography variant="body2">{arg.dayNumberText}</Typography>
        {totalPersonas > 0 && (
          <Typography
            variant="caption"
            sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: '0.65rem', mt: -0.5 }}
          >
            {totalPersonas} {totalPersonas === 1 ? 'persona' : 'personas'}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: '600px',
        '& .fc': { fontFamily: theme.typography.fontFamily },
        '& .fc-toolbar-title': { fontSize: '1.25rem', fontWeight: 600 },
        '& .fc-button-primary': {
          bgcolor: theme.palette.primary.main,
          borderColor: theme.palette.primary.main,
          textTransform: 'capitalize',
          '&:hover': {
            bgcolor: theme.palette.primary.dark,
            borderColor: theme.palette.primary.dark,
          },
        },
        '& .fc-daygrid-day-number': { color: theme.palette.text.primary, fontWeight: 500 },
        '& .fc-highlight': { bgcolor: 'rgba(105, 108, 255, 0.16)' },
        '& .fc-day-today': { bgcolor: 'rgba(105, 108, 255, 0.04) !important' },
      }}
    >
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        buttonText={{ today: 'Hoy' }}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: '', // Just month view
        }}
        selectable={true}
        selectMirror={true}
        unselectAuto={false}
        selectAllow={(selectInfo) => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return selectInfo.start >= today;
        }}
        dayMaxEvents={EVENT_LIMIT}
        moreLinkContent={(args) => `+${args.num} más`}
        events={events}
        eventContent={renderEventContent}
        dayCellContent={renderDayCellContent}
        select={(info) => {
          // info.end is exclusive in FullCalendar, we adjust it if needed in parent
          onDateSelect(info.start, info.end);
        }}
        dateClick={(info) => {
          onDateClick(info.date);
        }}
        eventClick={(info) => {
          const demand = info.event.extendedProps as DemandItem;
          onEventClick(demand);
        }}
        height="100%"
        locale="es"
        firstDay={1}
      />
    </Box>
  );
};
